import { useEffect, useRef, useState } from "react";
import {
  Paperclip,
  Sparkles,
  X,
  FileText,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../../components/ui/textarea";
import ChecklistVerificaOfferta from "../../components/cliente/ChecklistVerificaOfferta";
import AttachmentVisionAnalysis from "../../components/AttachmentVisionAnalysis";
import PdfTableImporter from "../../components/PdfTableImporter";
import { api, formatErrorDetail } from "../../lib/api";

const SAMPLE = `Buongiorno,

ci servirebbero 3 valvole a farfalla DN100 per la centrale termica.

Grazie,
Mario Rossi
Tintoria del Valdarno SRL`;

const PLACEHOLDER_BY_CHANNEL = {
  email_manual: "Incolla qui il testo della mail ricevuta…",
  phone: "Trascrivi la richiesta telefonica (cliente, prodotto, quantità, requisiti)…",
  whatsapp: "Incolla qui la conversazione WhatsApp…",
  manual: "Inserisci una nota libera oppure compila direttamente la griglia accanto…",
  email_auto: "Email importata automaticamente — il testo originale è già caricato.",
};

function fileIcon(file) {
  const type = file.type || "";
  if (type.startsWith("image/")) return ImageIcon;
  return FileText;
}

export default function InputPane({
  onAnalyze,
  onOpenSession,
  onAddVoce,
  loading,
  error,
  channel,
  sessionId,
  initialChecklist,
  shippingNode,
}) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [pdfTables, setPdfTables] = useState([]);
  const [pdfImporterOpen, setPdfImporterOpen] = useState(false);
  const [scanningPdf, setScanningPdf] = useState(false);
  const dropRef = useRef(null);

  const refreshRecent = () => {
    api
      .recent(5)
      .then((d) => setRecent(d.items || []))
      .catch(() => setRecent([]))
      .finally(() => setLoadingRecent(false));
  };

  useEffect(() => {
    refreshRecent();
  }, []);

  // Re-fetch after a successful analyze (new session should appear)
  useEffect(() => {
    if (!loading) refreshRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove("tq-drop-active");
    const dropped = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...dropped]);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add("tq-drop-active");
  };
  const onDragLeave = () => dropRef.current?.classList.remove("tq-drop-active");

  const submit = async () => {
    if (!text.trim() || loading) return;
    // R10 v0.5.8 — accetta anche immagini (Vision OCR), oltre ai PDF
    const acceptable = files.filter(
      (f) =>
        f.type === "application/pdf" ||
        (f.type || "").startsWith("image/") ||
        /\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(f.name || "")
    );
    await onAnalyze(text, acceptable);
  };

  const hasScannableFile = files.some(
    (f) =>
      f.type === "application/pdf" ||
      (f.type || "").startsWith("image/") ||
      /\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(f.name || "")
  );

  return (
    <aside
      className="flex flex-col h-full overflow-hidden bg-white"
      style={{ borderRight: "1px solid var(--border-subtle)" }}
      data-testid="input-pane"
    >
      {/* Section header */}
      <header className="px-6 pt-6 pb-3">
        <div className="tq-section-label mb-1.5">Nuova richiesta</div>
        <p
          className="text-[13px]"
          style={{ color: "var(--text-muted)" }}
        >
          Incolla il testo o trascina un file
        </p>
      </header>

      <div className="flex-1 overflow-auto tq-scroll px-6 pb-6 space-y-5">
        {/* Textarea */}
        <div>
          <Textarea
            data-testid="email-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              PLACEHOLDER_BY_CHANNEL[channel] ||
              "Buongiorno, ci servirebbero 3 valvole a farfalla DN100…"
            }
            rows={12}
            disabled={loading}
            className="text-[14px] leading-[20px] resize-none"
            style={{
              fontFamily: "Inter, sans-serif",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              minHeight: "280px",
              padding: "14px 16px",
              color: "var(--text-primary)",
              background: "var(--bg-surface)",
            }}
          />
          <div
            className="flex items-center justify-end gap-2 mt-2 text-[12px]"
            style={{ color: "var(--text-muted)" }}
          >
            <button
              type="button"
              onClick={() => {
                setText("");
                setFiles([]);
              }}
              disabled={loading}
              className="hover:text-[var(--text-primary)] transition-colors disabled:opacity-40"
              data-testid="clear-btn"
            >
              Cancella
            </button>
            <span aria-hidden>·</span>
            <button
              type="button"
              onClick={() => setText(SAMPLE)}
              disabled={loading}
              className="hover:text-[var(--text-primary)] transition-colors disabled:opacity-40"
              data-testid="load-sample-btn"
            >
              Esempio
            </button>
          </div>
        </div>

        {/* Dropzone (compact R3 FIX J) */}
        <div
          ref={dropRef}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className="rounded-md transition-all px-3 py-2 flex items-center gap-3"
          style={{
            border: "2px dashed var(--border-subtle)",
            background: "var(--bg-app)",
          }}
          data-testid="dropzone"
        >
          <Paperclip
            className="h-4 w-4 shrink-0"
            style={{ color: "var(--text-muted)" }}
          />
          <p
            className="text-[12px] flex-1 truncate"
            style={{ color: "var(--text-secondary)" }}
          >
            Trascina file o
          </p>
          <label
            className="inline-flex items-center px-2 h-7 rounded-md text-[11px] font-medium cursor-pointer transition-colors shrink-0"
            style={{
              border: "1px solid var(--border-default)",
              color: "var(--text-primary)",
              background: "var(--bg-surface)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--bg-subtle)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--bg-surface)")
            }
          >
            seleziona
            <input
              type="file"
              multiple
              onChange={(e) =>
                setFiles((prev) => [...prev, ...Array.from(e.target.files || [])])
              }
              className="hidden"
              data-testid="file-input"
            />
          </label>
        </div>

        {files.length > 0 && (
          <ul className="space-y-1.5" data-testid="file-list">
            {files.map((f, i) => {
              const Icon = fileIcon(f);
              const isPdf = f.type === "application/pdf";
              const isImage =
                (f.type || "").startsWith("image/") ||
                /\.(png|jpe?g|webp|heic|heif)$/i.test(f.name || "");
              const isScannable = isPdf || isImage;
              return (
                <li
                  key={i}
                  className={`flex items-center justify-between text-[12px] rounded-md px-2.5 py-1.5 ${
                    isScannable ? "" : "opacity-80"
                  }`}
                  style={{
                    border: `1px solid ${
                      isScannable ? "var(--border-subtle)" : "#FEF3C7"
                    }`,
                    background: isScannable ? "var(--bg-surface)" : "#FFFBEB",
                    color: isScannable ? "var(--text-primary)" : "#92400E",
                  }}
                >
                  <span className="flex items-center gap-2 truncate">
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate font-mono">{f.name}</span>
                    {isImage && (
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
                        style={{
                          background: "var(--accent-subtle)",
                          color: "var(--accent-primary)",
                        }}
                      >
                        OCR
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, idx) => idx !== i))
                    }
                    className="ml-2 opacity-50 hover:opacity-100 transition-opacity"
                    aria-label="Rimuovi"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
            {files.some(
              (f) =>
                f.type !== "application/pdf" &&
                !(f.type || "").startsWith("image/") &&
                !/\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(f.name || "")
            ) && (
              <li className="text-[11px] italic" style={{ color: "#A16207" }}>
                solo PDF e immagini saranno letti — gli altri file restano allegati
              </li>
            )}
            {hasScannableFile && (
              <li
                className="text-[11px] italic flex items-start gap-1.5"
                style={{ color: "var(--text-muted)" }}
                data-testid="ocr-hint"
              >
                <span aria-hidden></span>
                Sto leggendo il PDF/immagine — può richiedere 10–60s per scansioni.
              </li>
            )}
          </ul>
        )}

        {/* R6 — Vision foto + PDF tables (active session only) */}
        {sessionId && (
          <div className="space-y-1.5" data-testid="r6-helpers">
            <AttachmentVisionAnalysis
              offerId={sessionId}
              onAddVoce={onAddVoce}
            />
            {files.some((f) => f.type === "application/pdf") && (
              <button
                type="button"
                onClick={async () => {
                  const pdf = files.find((f) => f.type === "application/pdf");
                  if (!pdf) return;
                  setScanningPdf(true);
                  try {
                    const res = await api.pdfTables(sessionId, pdf);
                    if ((res.tables || []).length === 0) {
                      toast.info("Nessuna tabella rilevata in questo PDF");
                    } else {
                      setPdfTables(res.tables);
                      setPdfImporterOpen(true);
                    }
                  } catch (err) {
                    toast.error(formatErrorDetail(err));
                  } finally {
                    setScanningPdf(false);
                  }
                }}
                disabled={scanningPdf}
                className="w-full inline-flex items-center justify-center gap-1 h-6 rounded text-[11px] font-medium disabled:opacity-40"
                style={{
                  background: "var(--accent-subtle)",
                  color: "var(--accent-primary)",
                  border: "1px solid #BFDBFE",
                }}
                data-testid="scan-pdf-tables-btn"
              >
                {scanningPdf ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" /> Scansione…
                  </>
                ) : (
                  <>
                    <FileText className="h-3 w-3" /> Rileva tabelle nel PDF
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {error && (
          <div
            className="rounded-md px-3 py-2 text-[12px]"
            style={{
              background: "var(--semantic-red-bg)",
              color: "var(--semantic-red)",
              border: "1px solid #FECACA",
            }}
            data-testid="analyze-error"
          >
            {error}
          </div>
        )}

        {/* Primary CTA */}
        <button
          type="button"
          disabled={loading || !text.trim()}
          onClick={submit}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md font-semibold text-[14px] transition-all disabled:cursor-not-allowed"
          style={{
            background:
              loading || !text.trim()
                ? "var(--bg-subtle)"
                : "var(--accent-primary)",
            color:
              loading || !text.trim()
                ? "var(--text-muted)"
                : "var(--text-on-accent)",
            boxShadow: !loading && text.trim() ? "var(--shadow-sm)" : "none",
          }}
          onMouseEnter={(e) => {
            if (!loading && text.trim())
              e.currentTarget.style.background = "var(--accent-hover)";
          }}
          onMouseLeave={(e) => {
            if (!loading && text.trim())
              e.currentTarget.style.background = "var(--accent-primary)";
          }}
          data-testid="analyze-btn"
          title={
            hasScannableFile && !loading
              ? "Lettura tramite OCR — può richiedere 10–60s per scansioni"
              : undefined
          }
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {hasScannableFile
                ? "Lettura PDF/immagine in corso…"
                : "Analisi in corso…"}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Analizza richiesta
            </>
          )}
        </button>

        {/* R4 FIX H+I — replaced "Storico recenti" with Checklist + Shipping */}
        {sessionId && (
          <section className="pt-2 space-y-3">
            <ChecklistVerificaOfferta
              offerId={sessionId}
              initial={initialChecklist}
            />
            {shippingNode}
          </section>
        )}
      </div>

      {/* R6 — PDF table importer modal (portal) */}
      <PdfTableImporter
        open={pdfImporterOpen}
        onClose={() => setPdfImporterOpen(false)}
        tables={pdfTables}
        offerId={sessionId}
        onImported={() => {
          setPdfTables([]);
          // Signal parent to reload session
          onOpenSession?.(sessionId);
        }}
      />
    </aside>
  );
}

function labelStatus(s) {
  return (
    {
      analyzing: "in analisi",
      awaiting_info: "info da richiedere",
      matching: "in lavorazione",
      completed: "completata",
    }[s] || s
  );
}
